import React, { ComponentProps, useCallback, useMemo } from 'react'
import { GiphyEntryDesktop } from '@components/Giphy/GiphyEntry'
import { ShortcutTooltip } from '@components/Shortcuts/ShortcutTooltip'
import { withRef } from '@udecode/cn'
import {
    BoldPlugin,
    CodePlugin,
    ItalicPlugin,
    StrikethroughPlugin,
    UnderlinePlugin,
} from '@udecode/plate-basic-marks/react'
import { useEditorRef } from '@udecode/plate-common/react'
import { BaseBulletedListPlugin, BaseNumberedListPlugin } from '@udecode/plate-list'
import { Box, BoxProps, DividerEditorToolbar, IconButton, MotionBox, Stack } from '@ui'
import { useDevice } from 'hooks/useDevice'

import { BlockQuoteToolbarButton } from './BlockQuoteToolbarButton'
import { CodeBlockToolbarButton } from './CodeBlockToolbarButton'
import { LinkToolbarButton } from './LinkToolbarButton'
import { ListToolbarButton } from './ListToolbarButton'
import { MarkToolbarButton } from './MarkToolbarButton'

export type FormattingToolbarProps = {
    editing?: boolean
    focused: boolean
    readOnly?: boolean
    background?: BoxProps['background']
    isFloating?: boolean
    onLinkClick: () => void
    attemptingToSend?: boolean
    rounded?: BoxProps['rounded']
    showFormattingToolbar: boolean
    setIsFormattingToolbarOpen: (isFormattingToolbarOpen: boolean) => void
} & ComponentProps<typeof GiphyEntryDesktop>

export const FormattingToolbar = withRef<'div', FormattingToolbarProps>(
    (
        {
            showFormattingToolbar,
            setIsFormattingToolbarOpen,
            focused,
            isFloating,
            onLinkClick,
            background = 'level2',
        },
        ref,
    ) => {
        const { isTouch } = useDevice()
        const editor = useEditorRef()

        const onToolbarPointerDown = useCallback((e: React.PointerEvent) => {
            e.preventDefault()
        }, [])

        const closeFormattingToolbar = useCallback(
            (e: React.TouchEvent) => {
                e.stopPropagation()
                e.preventDefault()
                setIsFormattingToolbarOpen(false)
            },
            [setIsFormattingToolbarOpen],
        )

        const toolbarAnimationProps = {
            initial: isFloating
                ? { opacity: 0, y: 4 }
                : isTouch
                  ? { opacity: 0, width: 0 }
                  : { opacity: 0, height: 0 },
            animate: isFloating
                ? { opacity: 1, y: 0 }
                : isTouch
                  ? { opacity: 1, width: 'auto' }
                  : { opacity: 1, height: 'auto' },
            exit: isFloating
                ? { opacity: 0, transition: { duration: 0 } }
                : isTouch
                  ? { opacity: 0, width: 0, transition: { duration: 0 } }
                  : { opacity: 0, height: 0 },
        } as const

        const { floatingProps, floatingContainerProps } = useMemo(() => {
            if (isFloating) {
                return {
                    floatingProps: {
                        padding: 'xs',
                        rounded: 'sm',

                        border: 'faint',
                        style: { boxShadow: '6px 2px 6px rgba(0,0,0,0.1)' },
                    } as BoxProps,
                    floatingContainerProps: {
                        padding: 'paragraph',
                    } as BoxProps,
                }
            }
            return { floatingProps: {}, floatingContainerProps: {} }
        }, [isFloating])

        return (
            <div ref={ref as React.Ref<HTMLDivElement>}>
                {showFormattingToolbar && (
                    <MotionBox overflow="hidden" {...toolbarAnimationProps}>
                        <Box
                            paddingTop={isTouch ? 'none' : 'sm'}
                            paddingX="sm"
                            data-testid="editor-formatting-toolbar"
                            {...floatingContainerProps}
                        >
                            <Stack
                                horizontal
                                background={background}
                                overflowX="scroll"
                                gap={isTouch ? 'sm' : 'xs'}
                                pointerEvents="auto"
                                width="100%"
                                opacity={focused ? 'opaque' : '0.4'}
                                zIndex="tooltips"
                                onPointerDown={onToolbarPointerDown}
                                {...floatingProps}
                            >
                                {isTouch && (
                                    <IconButton
                                        opaque
                                        icon="close"
                                        background={background}
                                        onTouchStart={closeFormattingToolbar}
                                    />
                                )}
                                <MarkToolbarButton
                                    nodeType={BoldPlugin.key}
                                    icon="bold"
                                    background={background}
                                    tooltip={ShortcutTooltip({ action: 'BoldText' })}
                                />
                                <MarkToolbarButton
                                    nodeType={ItalicPlugin.key}
                                    icon="italic"
                                    background={background}
                                    tooltip={ShortcutTooltip({ action: 'ItalicText' })}
                                />
                                <MarkToolbarButton
                                    nodeType={UnderlinePlugin.key}
                                    icon="underline"
                                    background={background}
                                    tooltip={ShortcutTooltip({ action: 'UnderlineText' })}
                                />
                                <MarkToolbarButton
                                    nodeType={StrikethroughPlugin.key}
                                    icon="strikethrough"
                                    background={background}
                                    tooltip="Strikethrough"
                                />
                                <LinkToolbarButton background={background} onClick={onLinkClick} />
                                <DividerEditorToolbar />
                                <ListToolbarButton
                                    nodeType={BaseNumberedListPlugin.key}
                                    icon="numberedlist"
                                    tooltip="Ordered list"
                                    editor={editor}
                                    background={background}
                                />
                                <ListToolbarButton
                                    nodeType={BaseBulletedListPlugin.key}
                                    icon="bulletedlist"
                                    tooltip="Bulleted list"
                                    editor={editor}
                                    background={background}
                                />
                                <DividerEditorToolbar />
                                <BlockQuoteToolbarButton background={background} />
                                <DividerEditorToolbar />
                                <MarkToolbarButton
                                    nodeType={CodePlugin.key}
                                    icon="code"
                                    background={background}
                                    tooltip="Code"
                                />
                                <CodeBlockToolbarButton background={background} />
                            </Stack>
                        </Box>
                    </MotionBox>
                )}
            </div>
        )
    },
)
